import React, { useEffect, useMemo, useState } from "react";
import Loader from "react-spinner-loader";
import {
  X,
  History as HistoryIcon,
  PlayCircle,
  Loader as LoaderIcon,
  CheckCircle2,
  XCircle,
  ClipboardList,
  Package,
  Paperclip,
  Timer,
  CalendarClock,
  AlertTriangle,
  MapPin,
  Sheet,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Eye,
  Info,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getData } from "../../../../../Services/Services";
import { getSopInstanceHistory } from "../../../../../Services/EndPoints";
import PreviewFile from "../../../Common/Form/PreviewFile/PreviewFile";

const FLOW_EXPORT_COLUMNS = [
  "Step",
  "SOP Instance ID",
  "Action",
  "Date & Time",
  "Performed By",
  "Status",
  "Task ID",
  "Task Name",
  "Role",
  "Assigned To",
  "Priority",
  "Resource",
  "Quantity",
  "Remarks",
];

const RESOURCES_EXPORT_COLUMNS = ["Resource", "Facility", "Agency", "Total Qty", "Damaged Qty", "Deployed Qty", "Available Qty", "Manual", "Auto"];

const exportFileStamp = () => {
  const p = (n) => String(n).padStart(2, "0");
  const n = new Date();
  return `${p(n.getDate())}-${p(n.getMonth() + 1)}-${n.getFullYear()}_${p(n.getHours())}${p(n.getMinutes())}${p(n.getSeconds())}`;
};

// Some attachment URLs come back from the backend with a stray trailing
// `%22` (a percent-encoded quote character) or literal quote — strip it so
// extension detection and previewing still work.
const sanitizeAttachmentUrl = (url = "") => String(url).replace(/(%22|["'])+$/g, "").trim();

const getFileNameFromUrl = (url = "") => {
  try {
    const decoded = decodeURIComponent(String(url).split("?")[0]);
    const parts = decoded.split("/");
    return parts[parts.length - 1] || url;
  } catch {
    return url;
  }
};

const isImageUrl = (url = "") => /\.(png|jpe?g|gif|webp|bmp)$/i.test(String(url).split("?")[0]);

// A single attachments[] entry can itself be a comma-separated list of URLs
// (backend quirk) — split every entry and sanitize/dedupe down to a flat
// list of individual URLs.
const flattenAttachments = (attachments) => {
  if (!attachments || !attachments.length) return [];
  return attachments
    .flatMap((entry) => String(entry).split(","))
    .map((url) => sanitizeAttachmentUrl(url.trim()))
    .filter(Boolean);
};

const badgeBase = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  lineHeight: 1.6,
};

const showValue = (value) => (value === null || value === undefined || value === "" ? "NA" : value);

const formatDateTime = (value) => {
  if (!value) return "NA";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh24 = d.getHours();
  const ampm = hh24 >= 12 ? "PM" : "AM";
  const hh = String(hh24 % 12 || 12).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy}, ${hh}:${min} ${ampm}`;
};

// Rule-based color/icon per action keyword — same convention used for stage
// colors elsewhere in the app (cancel/fail = red, closed/completed = green,
// progress = amber, executed = blue, default = purple).
const ACTION_RULES = [
  { test: /cancel/i, color: "#c0392b", bg: "#fde5e5", border: "#f2b8b8", icon: XCircle },
  { test: /fail/i, color: "#c0392b", bg: "#fde5e5", border: "#f2b8b8", icon: XCircle },
  { test: /clos/i, color: "#1e7e34", bg: "#e6f4ea", border: "#b7e1c1", icon: CheckCircle2 },
  { test: /complet/i, color: "#1e7e34", bg: "#e6f4ea", border: "#b7e1c1", icon: CheckCircle2 },
  { test: /progress/i, color: "#b26a00", bg: "#fff4e0", border: "#f2d9a8", icon: LoaderIcon },
  { test: /execut/i, color: "#1d5fa8", bg: "#e7f0fb", border: "#c2d9f0", icon: PlayCircle },
];
const getActionMeta = (action) => {
  const rule = ACTION_RULES.find((r) => r.test.test(action || ""));
  return rule
    ? rule
    : { color: "#6b46c1", bg: "#f1e9fd", border: "#ddd0f5", icon: ClipboardList };
};

// "SOP_EXECUTED" -> "SOP Executed"
const toLabel = (value) =>
  (value || "")
    .split("_")
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ") || "NA";

// Flattens the SOP flow (oldest first) into export rows — one row per task or
// resource per event, tagged with a running Step number so rows of the same
// event stay grouped.
const NA = "-";
const buildFlowRows = (chronologicalEvents, sopInstanceLabel) => {
  const rows = [];

  chronologicalEvents.forEach((event, idx) => {
    const common = {
      Step: idx + 1,
      "SOP Instance ID": showValue(sopInstanceLabel),
      Action: toLabel(event.action),
      "Date & Time": formatDateTime(event.eventTime),
      "Performed By": showValue(event.changedByName),
    };
    const blank = { "Task ID": NA, "Task Name": NA, Role: NA, "Assigned To": NA, Priority: NA, Resource: NA, Quantity: NA, Remarks: NA };

    const resourcesAdded = event.resources?.added || [];
    const assigned = event.tasks?.assigned || [];
    const claimed = event.tasks?.claimed || [];
    const completed = event.tasks?.completed || [];

    resourcesAdded.forEach((r) => {
      rows.push({ ...common, ...blank, Status: "Resource Added", Resource: showValue(r.name), Quantity: showValue(r.quantity) });
    });

    assigned.forEach((t) => {
      rows.push({
        ...common, ...blank, Status: "Task Assigned",
        "Task ID": showValue(t.taskSeqId), "Task Name": showValue(t.taskName), Role: showValue(t.roleName),
        "Assigned To": showValue(t.assignedToName), Priority: showValue(t.priorityLabel),
      });
    });

    claimed.forEach((t) => {
      rows.push({
        ...common, ...blank, Status: "Task In Progress",
        "Task ID": showValue(t.taskSeqId), "Task Name": showValue(t.taskName), Role: showValue(t.roleName),
        Remarks: showValue(t.remarks),
      });
    });

    completed.forEach((t) => {
      rows.push({
        ...common, ...blank, Status: "Task Completed",
        "Task ID": showValue(t.taskSeqId), "Task Name": showValue(t.taskName), Role: showValue(t.roleName),
        Remarks: showValue(t.remarks),
      });
    });

    if (!resourcesAdded.length && !assigned.length && !claimed.length && !completed.length) {
      rows.push({ ...common, ...blank, Status: toLabel(event.action), "Task Name": "No task or resource details" });
    }
  });

  return rows;
};

const sectionPalette = {
  resources: { color: "#1d5fa8", headBg: "#e7f0fb", border: "#c9dcf0" },
  assigned: { color: "#7c3aed", headBg: "#f1e9fd", border: "#ddd0f5" },
  claimed: { color: "#b26a00", headBg: "#fff4e0", border: "#f2d9a8" },
  completed: { color: "#1e7e34", headBg: "#e6f4ea", border: "#bfe0c8" },
};

const SectionCard = ({ icon: Icon, title, palette, children, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;

  return (
    <div style={{ border: `1px solid ${palette.border}`, borderRadius: "8px", overflow: "hidden", marginBottom: "10px" }}>
      <div
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        style={{
          display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px", background: palette.headBg,
          cursor: collapsible ? "pointer" : "default", userSelect: "none",
        }}
      >
        <Icon size={13} color={palette.color} />
        <span style={{ fontSize: "11.5px", fontWeight: 700, color: palette.color }}>{title}</span>
        {collapsible && (
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
            {isOpen ? <ChevronUp size={14} color={palette.color} /> : <ChevronDown size={14} color={palette.color} />}
          </span>
        )}
      </div>
      {isOpen && <div style={{ padding: "8px 10px" }}>{children}</div>}
    </div>
  );
};

const AttachmentThumb = ({ url: rawUrl, onPreview }) => {
  const url = sanitizeAttachmentUrl(rawUrl);
  const name = getFileNameFromUrl(url);
  if (isImageUrl(url)) {
    return (
      <img
        src={url}
        alt={name}
        title={name}
        onClick={() => onPreview({ name, url })}
        style={{
          width: "44px", height: "44px", objectFit: "cover", borderRadius: "6px",
          border: "1px solid #d0d7de", cursor: "pointer", flexShrink: 0,
        }}
      />
    );
  }
  return (
    <span
      title={name}
      onClick={() => onPreview({ name, url })}
      style={{
        display: "inline-flex", alignItems: "center", gap: "4px",
        fontSize: "10.5px", padding: "2px 8px", borderRadius: "10px",
        background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", cursor: "pointer",
      }}
    >
      <Paperclip size={10} /> {name}
    </span>
  );
};

const AttachmentChips = ({ attachments, onPreview, onPreviewGallery }) => {
  const urls = flattenAttachments(attachments);
  if (urls.length === 0) return <span style={{ color: "#8a97a3", fontSize: "11px" }}>No attachments</span>;

  if (urls.length > 1) {
    return (
      <span
        onClick={() => onPreviewGallery(urls.map((url) => ({ name: getFileNameFromUrl(url), url })))}
        style={{
          display: "inline-flex", alignItems: "center", gap: "4px",
          fontSize: "10.5px", fontWeight: 600, padding: "3px 10px", borderRadius: "10px",
          background: "#e7f0fb", color: "#1d5fa8", border: "1px solid #c9dcf0", cursor: "pointer",
        }}
      >
        <Eye size={11} /> View ({urls.length})
      </span>
    );
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
      <AttachmentThumb url={urls[0]} onPreview={onPreview} />
    </div>
  );
};

const AttachmentGalleryModal = ({ images, index, onIndexChange, onClose }) => {
  if (!images || images.length === 0) return null;
  const current = images[index];
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(17,28,38,0.8)", zIndex: 3600,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
        fontFamily: "'Poppins', 'Segoe UI', sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", maxWidth: "92vw", maxHeight: "92vh", display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: "10px" }}>
          <span style={{ color: "#fff", fontSize: "12px", fontWeight: 600 }}>{index + 1} / {images.length}</span>
          <span onClick={onClose} title="Close" style={{ cursor: "pointer", color: "#fff", display: "flex" }}>
            <X size={20} />
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span
            onClick={() => hasPrev && onIndexChange(index - 1)}
            title="Previous"
            style={{
              cursor: hasPrev ? "pointer" : "default", opacity: hasPrev ? 1 : 0.3, color: "#fff",
              padding: "8px", background: "rgba(255,255,255,0.15)", borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <ChevronLeft size={22} />
          </span>

          {isImageUrl(current.url) ? (
            <img
              src={current.url}
              alt={current.name}
              style={{ maxWidth: "68vw", maxHeight: "68vh", borderRadius: "8px", objectFit: "contain", background: "#11181f" }}
            />
          ) : (
            <div style={{ background: "#fff", borderRadius: "8px", padding: "28px", textAlign: "center", minWidth: "260px" }}>
              <p style={{ fontSize: "13px", color: "#33414d" }}>Preview isn't available for this file type.</p>
              <a href={current.url} target="_blank" rel="noopener noreferrer">
                Open "{current.name}"
              </a>
            </div>
          )}

          <span
            onClick={() => hasNext && onIndexChange(index + 1)}
            title="Next"
            style={{
              cursor: hasNext ? "pointer" : "default", opacity: hasNext ? 1 : 0.3, color: "#fff",
              padding: "8px", background: "rgba(255,255,255,0.15)", borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <ChevronRight size={22} />
          </span>
        </div>
        <div style={{ color: "#fff", fontSize: "11px", marginTop: "10px", wordBreak: "break-word", textAlign: "center", maxWidth: "68vw" }}>
          {current.name}
        </div>
      </div>
    </div>
  );
};

const AssignedTaskTable = ({ rows }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "420px", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "24%" }} />
        <col style={{ width: "26%" }} />
        <col style={{ width: "20%" }} />
        <col style={{ width: "16%" }} />
        <col style={{ width: "14%" }} />
      </colgroup>
      <thead>
        <tr>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Task ID</th>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Task</th>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Assigned To</th>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Role</th>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Priority</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <tr key={r.taskId ?? idx} style={{ borderTop: idx === 0 ? "none" : "1px solid #f0f2f5" }}>
            <td style={{ textAlign: "left", fontSize: "11.5px", color: "#1D2939", padding: "6px", verticalAlign: "top", whiteSpace: "nowrap" }}>{showValue(r.taskSeqId)}</td>
            <td style={{ textAlign: "left", fontSize: "11.5px", color: "#1D2939", padding: "6px", verticalAlign: "top" }}>
              <span title={r.taskName || undefined} style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {showValue(r.taskName)}
              </span>
            </td>
            <td style={{ textAlign: "left", fontSize: "11.5px", color: "#1D2939", padding: "6px", verticalAlign: "top", wordBreak: "break-word" }}>{showValue(r.assignedToName)}</td>
            <td style={{ textAlign: "left", fontSize: "11.5px", color: "#1D2939", padding: "6px", verticalAlign: "top", wordBreak: "break-word" }}>{showValue(r.roleName)}</td>
            <td style={{ textAlign: "left", fontSize: "11.5px", color: "#1D2939", padding: "6px", verticalAlign: "top" }}>{showValue(r.priorityLabel)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TaskDetailTable = ({ rows, onPreview, onPreviewGallery }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "420px", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "26%" }} />
        <col style={{ width: "28%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "28%" }} />
      </colgroup>
      <thead>
        <tr>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Task ID</th>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Task</th>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Role</th>
          <th style={{ textAlign: "left", verticalAlign: "middle", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#334155", padding: "4px 6px" }}>Attachments</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <React.Fragment key={r.taskId ?? idx}>
            <tr style={{ borderTop: idx === 0 ? "none" : "1px solid #f0f2f5" }}>
              <td style={{ textAlign: "left", fontSize: "11.5px", color: "#1D2939", padding: "6px", verticalAlign: "top", whiteSpace: "nowrap" }}>{showValue(r.taskSeqId)}</td>
              <td style={{ textAlign: "left", fontSize: "11.5px", color: "#1D2939", padding: "6px", verticalAlign: "top" }}>
                <span title={r.taskName || undefined} style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {showValue(r.taskName)}
                </span>
              </td>
              <td style={{ textAlign: "left", fontSize: "11.5px", color: "#1D2939", padding: "6px", verticalAlign: "top", wordBreak: "break-word" }}>{showValue(r.roleName)}</td>
              <td style={{ textAlign: "left", padding: "6px", verticalAlign: "top" }}>
                <AttachmentChips attachments={r.attachments} onPreview={onPreview} onPreviewGallery={onPreviewGallery} />
              </td>
            </tr>
            <tr style={{ borderTop: "1px dashed #f0f2f5" }}>
              <td colSpan={4} style={{ fontSize: "11px", color: "#5b6b7a", padding: "4px 6px 8px", wordBreak: "break-word" }}>
                <strong style={{ color: "#33414d", fontWeight: 600 }}>Remarks:</strong> {showValue(r.remarks)}
              </td>
            </tr>
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

const MetaChip = ({ icon: Icon, label, value, title }) => {
  if (value === undefined || value === null || value === "") return null;
  return (
    <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "10.5px", color: "#475569" }}>
      {Icon && <Icon size={11} />}
      <strong style={{ color: "#33414d", fontWeight: 600 }}>{label}:</strong> {value}
    </span>
  );
};

const FlagMark = ({ on, label }) =>
  on ? (
    <Check size={14} color="#1e7e34" strokeWidth={3} aria-label={label + ": yes"} />
  ) : (
    <X size={14} color="#c0392b" strokeWidth={3} aria-label={label + ": no"} />
  );

const ResourcesAttachedTable = ({ rows }) => (
  <div style={{ overflowX: "auto", maxHeight: "160px", overflowY: "auto", border: "1px solid #c9dcf0", borderRadius: "8px" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "560px", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "22%" }} />
        <col style={{ width: "22%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: "8%" }} />
      </colgroup>
      <thead>
        <tr>
          <th style={{ position: "sticky", top: 0, textAlign: "left", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#1d5fa8", padding: "5px 6px" }}>Resource</th>
          <th style={{ position: "sticky", top: 0, textAlign: "left", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#1d5fa8", padding: "5px 6px" }}>Facility</th>
          <th style={{ position: "sticky", top: 0, textAlign: "left", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#1d5fa8", padding: "5px 6px" }}>Total</th>
          <th style={{ position: "sticky", top: 0, textAlign: "left", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#1d5fa8", padding: "5px 6px" }}>Damaged</th>
          <th style={{ position: "sticky", top: 0, textAlign: "left", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#1d5fa8", padding: "5px 6px" }}>Deployed</th>
          <th style={{ position: "sticky", top: 0, textAlign: "left", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#1d5fa8", padding: "5px 6px" }}>Available</th>
          <th style={{ position: "sticky", top: 0, textAlign: "center", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#1d5fa8", padding: "5px 6px" }}>Manual</th>
          <th style={{ position: "sticky", top: 0, textAlign: "center", fontSize: "10px", fontWeight: 700, color: "#fff", background: "#1d5fa8", padding: "5px 6px" }}>Auto</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <tr key={r.id ?? idx} style={{ borderTop: idx === 0 ? "none" : "1px solid #eef1f4" }}>
            <td style={{ textAlign: "left", fontSize: "11px", color: "#1D2939", padding: "5px 6px", verticalAlign: "top" }}>
              <span title={r.name || undefined} style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {showValue(r.name)}
              </span>
            </td>
            <td style={{ textAlign: "left", fontSize: "11px", color: "#1D2939", padding: "5px 6px", verticalAlign: "top" }}>
              <span
                title={[r.facility_name, r.agency_name].filter(Boolean).join(" — ") || undefined}
                style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {showValue(r.facility_name)}
              </span>
            </td>
            <td style={{ textAlign: "left", fontSize: "11px", color: "#1D2939", padding: "5px 6px", verticalAlign: "top" }}>{showValue(r.total_quantity)}</td>
            <td style={{ textAlign: "left", fontSize: "11px", color: "#1D2939", padding: "5px 6px", verticalAlign: "top" }}>{showValue(r.damaged_quantity)}</td>
            <td style={{ textAlign: "left", fontSize: "11px", color: "#1D2939", padding: "5px 6px", verticalAlign: "top" }}>{showValue(r.deployed_quantity)}</td>
            <td style={{ textAlign: "left", fontSize: "11px", color: "#1D2939", padding: "5px 6px", verticalAlign: "top" }}>{showValue(r.available_quantity)}</td>
            <td style={{ textAlign: "center", padding: "5px 6px", verticalAlign: "top" }}><FlagMark on={!!r.is_manual} label="Manual" /></td>
            <td style={{ textAlign: "center", padding: "5px 6px", verticalAlign: "top" }}><FlagMark on={!!r.is_automatic} label="Auto" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SopInstanceAuditHistoryModal = ({ show, onClose, sopInstanceId, sopInstanceSeqId }) => {
  const [loader, setLoader] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [error, setError] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [galleryImages, setGalleryImages] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showDescription, setShowDescription] = useState(false);
  const navigate = useNavigate();

  const openGallery = (images) => {
    setGalleryImages(images);
    setGalleryIndex(0);
  };

  useEffect(() => {
    if (!show) return;
    let cancelled = false;

    const fetchHistory = async () => {
      setLoader(true);
      setError("");
      setHistoryData(null);
      try {
        const resp = await getData(`${getSopInstanceHistory}${encodeURIComponent(sopInstanceId ?? "")}`);
        if (cancelled) return;
        setHistoryData(resp && typeof resp === "object" ? resp : null);
      } catch (e) {
        if (!cancelled) {
          console.error("Error fetching SOP instance audit history:", e);
          setError("Unable to load audit history.");
        }
      } finally {
        if (!cancelled) setLoader(false);
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [show, sopInstanceId]);

  // Newest event first, so the most recent change is visible without scrolling.
  // eventTime can be null, so sort by the trailing sequence number in eventId
  // (e.g. "SOPI-20260922-713-4" -> 4) rather than trusting eventTime or the
  // API's own array order, which has varied between response versions.
  const events = useMemo(() => {
    if (!Array.isArray(historyData?.timeline)) return [];
    const withSeq = historyData.timeline.map((event, idx) => {
      const seqMatch = /-(\d+)$/.exec(event?.eventId || "");
      return { event, seq: seqMatch ? Number(seqMatch[1]) : idx, idx };
    });
    withSeq.sort((a, b) => (b.seq - a.seq) || (b.idx - a.idx));
    return withSeq.map((w) => w.event);
  }, [historyData]);

  // Prefer the friendly status text from the linked incident (e.g. "SOP_COMPLETED")
  // over the numeric currentStatusId the API now sends; fall back to the
  // newest timeline event's action when the incident block isn't present.
  const currentStatusLabel = historyData?.incident?.currentStatus || events[0]?.action;
  const currentStatusMeta = getActionMeta(currentStatusLabel);
  const resourcesAttached = Array.isArray(historyData?.resourcesAttached) ? historyData.resourcesAttached : [];

  // gisConfig arrives as a JSON string like {"incidentcenter":[lng,lat]}.
  const incidentCenter = (() => {
    try {
      const cfg = JSON.parse(historyData?.incident?.gisConfig || "");
      const [lng, lat] = cfg?.incidentcenter || [];
      return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)) && lat !== undefined && lng !== undefined
        ? { lat: Number(lat), lng: Number(lng) }
        : null;
    } catch {
      return null;
    }
  })();

  // Opens the GIS map's Incident tab with this incident preselected (same
  // navigation state the Incident List's "View GIS Data" action uses).
  const openIncidentOnMap = () => {
    if (!historyData?.incident?.incidentId) return;
    navigate("/Disaster-acknowledgereport/situation-awareness", {
      state: { id: historyData.incident.incidentId, tabvv: true },
    });
  };

  const handleExportExcel = async () => {
    // events is newest-first for on-screen display; the export follows the SOP flow oldest-first.
    const chronologicalEvents = [...events].reverse();
    const flowRows = buildFlowRows(chronologicalEvents, sopInstanceSeqId || historyData?.sopInstanceSeqId || historyData?.sopInstanceId);
    if (!flowRows.length && !resourcesAttached.length && !historyData) {
      toast.info("No audit history to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const thin = { style: "thin", color: { argb: "FFD0D7DE" } };
    const border = { top: thin, left: thin, bottom: thin, right: thin };
    const BLUE = "FF2E6DA4";

    const styleHeader = (row) => {
      row.height = 24;
      row.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BLUE } };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = border;
      });
    };
    const styleDataRow = (row, fillArgb) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = { horizontal: "left", vertical: "top", wrapText: true };
        cell.border = border;
        if (fillArgb) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillArgb } };
      });
    };
    const addTitle = (ws, text, colCount) => {
      const row = ws.addRow([text]);
      ws.mergeCells(row.number, 1, row.number, colCount);
      row.height = 26;
      row.getCell(1).font = { bold: true, size: 14, color: { argb: BLUE } };
      row.getCell(1).alignment = { vertical: "middle" };
      ws.addRow([]);
    };

    const incident = historyData?.incident || {};
    const seqId = sopInstanceSeqId || historyData?.sopInstanceSeqId || historyData?.sopInstanceId || "";

    // ---- Sheet 1: Summary (what this SOP instance is) ----
    const wsSum = workbook.addWorksheet("Summary");
    addTitle(wsSum, `SOP Instance Audit History — ${seqId}`, 2);
    const summarySections = [
      ["SOP Details", [
        ["SOP Instance ID", seqId],
        ["SOP Name", historyData?.sopName],
        ["Trigger Type", historyData?.isAutoTriggered ? "Auto" : "Manual"],
        ["Current Status", currentStatusLabel ? toLabel(currentStatusLabel) : null],
        ["SLA (hours)", historyData?.slaHours],
        ["Target Date & Time", historyData?.targetDatetime ? formatDateTime(historyData.targetDatetime) : null],
        ["SOP Description", historyData?.description],
      ]],
      ["Lifecycle", [
        ["SOP Created On", historyData?.createdOn ? formatDateTime(historyData.createdOn) : null],
        ["SOP Created By", historyData?.createdBy],
        ["SOP Completed On", historyData?.completedOn ? formatDateTime(historyData.completedOn) : null],
        ["SOP Cancelled On", historyData?.cancelledOn ? formatDateTime(historyData.cancelledOn) : null],
        ["Cancel Reason", historyData?.cancelReason || historyData?.cancelRemarks],
      ]],
      ["Linked Incident", [
        ["Incident ID", incident.incidentSeqId],
        ["Incident Name", incident.incidentName],
        ["Hazard Type", incident.hazardType],
        ["Severity", incident.severity],
        ["Incident Stage", incident.currentStage],
        ["Incident Status", incident.currentStatus ? toLabel(incident.currentStatus) : null],
        ["Location (Lat, Long)", incidentCenter ? `${incidentCenter.lat}, ${incidentCenter.lng}` : null],
        ["Incident Created On", incident.createdOn ? formatDateTime(incident.createdOn) : null],
        ["Incident Description", incident.description],
      ]],
      ["Resource Deployment", [
        ["Resources Deployed", historyData?.resourceDeploymentSummary?.deployedCount],
        ["Resources Released", historyData?.resourceDeploymentSummary?.releasedCount],
      ]],
    ];
    summarySections.forEach(([heading, items]) => {
      const visible = items.filter(([, v]) => v !== null && v !== undefined && v !== "");
      if (!visible.length) return;
      const h = wsSum.addRow([heading, ""]);
      styleHeader(h);
      h.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
      visible.forEach(([label, value]) => {
        const r = wsSum.addRow([label, value]);
        styleDataRow(r);
        r.getCell(1).font = { bold: true };
        r.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
      });
      wsSum.addRow([]);
    });
    wsSum.getColumn(1).width = 26;
    wsSum.getColumn(2).width = 80;

    // ---- Sheet 2: SOP Flow (step by step, oldest first) ----
    if (flowRows.length) {
      const wsFlow = workbook.addWorksheet("SOP Flow");
      addTitle(wsFlow, "SOP Flow", FLOW_EXPORT_COLUMNS.length);
      const headerRow = wsFlow.addRow(FLOW_EXPORT_COLUMNS);
      styleHeader(headerRow);
      flowRows.forEach((r) => {
        // Alternate a light band per step so rows belonging to one event read as a group.
        styleDataRow(wsFlow.addRow(FLOW_EXPORT_COLUMNS.map((c) => r[c])), r.Step % 2 === 0 ? "FFF6F9FC" : null);
      });
      const widths = { Step: 7, "SOP Instance ID": 22, Action: 18, "Date & Time": 20, "Performed By": 18, Status: 18, "Task ID": 20, "Task Name": 32, Role: 16, "Assigned To": 18, Priority: 10, Resource: 30, Quantity: 10, Remarks: 36 };
      FLOW_EXPORT_COLUMNS.forEach((c, i) => { wsFlow.getColumn(i + 1).width = widths[c] || 16; });
      wsFlow.views = [{ state: "frozen", ySplit: headerRow.number }];
      wsFlow.autoFilter = { from: { row: headerRow.number, column: 1 }, to: { row: headerRow.number, column: FLOW_EXPORT_COLUMNS.length } };
    }

    // ---- Sheet 3: Resources Attached ----
    if (resourcesAttached.length) {
      const wsRes = workbook.addWorksheet("Resources Attached");
      addTitle(wsRes, "Resources Attached", RESOURCES_EXPORT_COLUMNS.length);
      const headerRow = wsRes.addRow(RESOURCES_EXPORT_COLUMNS);
      styleHeader(headerRow);
      resourcesAttached.forEach((r) =>
        styleDataRow(
          wsRes.addRow([
            showValue(r.name),
            showValue(r.facility_name),
            showValue(r.agency_name),
            showValue(r.total_quantity),
            showValue(r.damaged_quantity),
            showValue(r.deployed_quantity),
            showValue(r.available_quantity),
            r.is_manual ? "Yes" : "No",
            r.is_automatic ? "Yes" : "No",
          ])
        )
      );
      const widths = [34, 34, 36, 12, 12, 12, 12, 10, 10];
      widths.forEach((w, i) => { wsRes.getColumn(i + 1).width = w; });
      wsRes.views = [{ state: "frozen", ySplit: headerRow.number }];
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `SOP_Instance_Audit_History_${seqId || "sop_instance"}_${exportFileStamp()}.xlsx`);
  };

  if (!show) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(17,28,38,0.55)", zIndex: 3000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
        fontFamily: "'Poppins', 'Segoe UI', sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: "12px", width: "min(720px, 96vw)", maxHeight: "88vh",
          display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.28)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)" }}>
          <div style={{ width: "28px", height: "28px", borderRadius: "8px", background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <HistoryIcon size={14} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "14px", fontWeight: 800, color: "#fff", letterSpacing: ".2px" }}>SOP Instance Audit History</div>
            <div style={{ fontSize: "10.5px", color: "rgba(255,255,255,0.8)", marginTop: "1px" }}>Timeline of task &amp; resource activity for this SOP instance</div>
          </div>
          <span
            onClick={handleExportExcel}
            title="Download Excel"
            style={{ cursor: "pointer", width: "28px", height: "28px", borderRadius: "8px", background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: "8px" }}
          >
            <Sheet size={14} color="#fff" />
          </span>
          <span
            onClick={onClose}
            title="Close"
            style={{ cursor: "pointer", width: "28px", height: "28px", borderRadius: "8px", background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            <X size={16} color="#fff" />
          </span>
        </div>

        {/* Context bar */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", padding: "10px 16px", borderBottom: "1px solid #eef1f4", background: "#f8fafc" }}>
          <span style={{ ...badgeBase, background: "#1d5fa8", color: "#fff" }}>
            {showValue(sopInstanceSeqId || historyData?.sopInstanceSeqId || historyData?.sopInstanceId)}
          </span>
          <span style={{ fontSize: "11.5px", fontWeight: 600, color: "#1a2e35" }}>{showValue(historyData?.sopName)}</span>
          <span style={{ ...badgeBase, background: "#eef3f8", color: "#1a2e35", border: "1px solid #d9e2ea" }}>
            {historyData?.isAutoTriggered ? "Auto" : "Manual"}
          </span>
          {currentStatusLabel && (
            <span style={{ ...badgeBase, background: currentStatusMeta.bg, color: currentStatusMeta.color, border: `1px solid ${currentStatusMeta.border}` }}>
              {toLabel(currentStatusLabel)}
            </span>
          )}
        </div>

        {/* Meta row — incident context, SLA/target, and lifecycle timestamps */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "8px 18px", padding: "8px 16px", borderBottom: "1px solid #eef1f4", background: "#fff" }}>
          {historyData?.incident && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "3px" }}>
              <MetaChip
                icon={MapPin}
                label="Incident"
                value={historyData.incident.incidentName || historyData.incident.hazardType}
              />
              {historyData.incident.incidentSeqId && (
                <span style={{ ...badgeBase, padding: "0 8px", fontSize: "10px", background: "#eef3f8", color: "#1d5fa8", border: "1px solid #c9dcf0" }}>
                  {historyData.incident.incidentSeqId}
                </span>
              )}
            </div>
          )}
          {historyData?.incident?.description && (
            <span
              onClick={() => setShowDescription(true)}
              title="View incident details"
              style={{
                display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer",
                fontSize: "10.5px", fontWeight: 600, padding: "2px 10px", borderRadius: "10px",
                background: "#e7f0fb", color: "#1d5fa8", border: "1px solid #c9dcf0",
              }}
            >
              <Eye size={11} /> View Details
            </span>
          )}
          {incidentCenter && (
            <span
              onClick={openIncidentOnMap}
              title="Open this incident on the GIS map"
              style={{
                display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer",
                fontSize: "10.5px", color: "#1d5fa8", textDecoration: "underline",
              }}
            >
              <MapPin size={11} />
              <strong style={{ fontWeight: 600 }}>Lat/Long:</strong> {incidentCenter.lat.toFixed(4)}, {incidentCenter.lng.toFixed(4)}
            </span>
          )}
          <MetaChip icon={AlertTriangle} label="Severity" value={historyData?.incident?.severity} />
          <MetaChip icon={Timer} label="SLA" value={historyData?.slaHours ? `${historyData.slaHours} hrs` : null} />
          <MetaChip
            icon={CalendarClock}
            label="Target"
            value={historyData?.targetDatetime ? formatDateTime(historyData.targetDatetime) : null}
          />
          <MetaChip
            label="SOP Created"
            value={historyData?.createdOn ? `${formatDateTime(historyData.createdOn)}${historyData?.createdBy ? ` by ${historyData.createdBy}` : ""}` : null}
          />
          <MetaChip
            label="SOP Completed"
            value={historyData?.completedOn ? formatDateTime(historyData.completedOn) : null}
          />
          <MetaChip
            label="Cancelled"
            value={
              historyData?.cancelledOn
                ? `${formatDateTime(historyData.cancelledOn)}${historyData?.cancelRemarks || historyData?.cancelReason ? ` — ${historyData.cancelRemarks || historyData.cancelReason}` : ""}`
                : null
            }
          />
        </div>

        {/* Body */}
        <div style={{ position: "relative", overflowY: "auto", padding: "18px 20px" }}>
          <Loader show={loader} type="body" message="Loading Audit History" />

          {!loader && error && (
            <div style={{ textAlign: "center", color: "#c0392b", fontSize: "12px", padding: "30px 0" }}>{error}</div>
          )}

          {!loader && !error && events.length === 0 && (
            <div style={{ textAlign: "center", color: "#5b6b7a", fontSize: "12px", padding: "30px 0" }}>
              No audit history found for this SOP instance.
            </div>
          )}

          {!loader && !error && resourcesAttached.length > 0 && (
            <SectionCard icon={Package} title="Resources Attached" palette={sectionPalette.resources} collapsible defaultOpen={true}>
              <ResourcesAttachedTable rows={resourcesAttached} />
              {historyData?.resourceDeploymentSummary && (
                <div
                  title="Resource deployment summary"
                  style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", fontSize: "10.5px", color: "#475569" }}
                >
                  <Info size={13} color="#1d5fa8" />
                  <span>
                    <strong style={{ color: "#33414d", fontWeight: 600 }}>Deployed:</strong> {showValue(historyData.resourceDeploymentSummary.deployedCount)}
                    <span style={{ margin: "0 8px", color: "#c3ccd6" }}>|</span>
                    <strong style={{ color: "#33414d", fontWeight: 600 }}>Released:</strong> {showValue(historyData.resourceDeploymentSummary.releasedCount)}
                  </span>
                </div>
              )}
            </SectionCard>
          )}

          {!loader && !error && events.length > 0 && (
            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", margin: "4px 0 14px", paddingBottom: "6px", borderBottom: "2px solid #2E6DA4" }}>
              <span style={{ fontSize: "13px", fontWeight: 800, color: "#2E6DA4", letterSpacing: ".2px" }}>SOP Flow</span>
              <span style={{ fontSize: "10.5px", color: "#5b6b7a" }}>Activity timeline</span>
            </div>
          )}

          {!loader && !error && events.length > 0 && (
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: "15px", top: "6px", bottom: "6px", width: "3px", borderRadius: "2px", background: "#c3ccd6" }} />
              {events.map((event, idx) => {
                const meta = getActionMeta(event.action);
                const Icon = meta.icon;
                const isLatest = idx === 0;
                const assigned = event.tasks?.assigned;
                const claimed = event.tasks?.claimed;
                const completed = event.tasks?.completed;
                const hasDetails = !!(
                  (assigned && assigned.length) ||
                  (claimed && claimed.length) ||
                  (completed && completed.length)
                );
                const taskCount = (assigned?.length || 0) + (claimed?.length || 0) + (completed?.length || 0);
                const isSopExecuted = /execut/i.test(event.action || "");
                const eventTimeTitle = isSopExecuted ? `${toLabel(event.action)} Date and Time` : "Updated Date and Time";

                return (
                  <div key={event.eventId || `${event.action}-${idx}`} style={{ position: "relative", paddingLeft: "42px", marginBottom: idx === events.length - 1 ? 0 : "16px" }}>
                    <div
                      style={{
                        position: "absolute", left: 0, top: 0, width: "32px", height: "32px", borderRadius: "50%",
                        background: meta.bg, border: `2px solid ${meta.color}`, display: "flex", alignItems: "center",
                        justifyContent: "center", zIndex: 1, boxShadow: isLatest ? `0 0 0 4px ${meta.bg}` : "none",
                      }}
                    >
                      <Icon size={14} color={meta.color} />
                    </div>

                    <div style={{ border: `1px solid ${meta.border}`, borderRadius: "10px", overflow: "hidden", background: "#fff" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", padding: "8px 12px", background: meta.bg, borderBottom: `1px solid ${meta.border}` }}>
                        <span style={{ ...badgeBase, background: meta.color, color: "#fff" }}>{toLabel(event.action)}</span>
                        {isSopExecuted && taskCount > 0 && (
                          <span style={{ fontSize: "10.5px", fontWeight: 600, color: meta.color }}>
                            Total Tasks: {taskCount}
                          </span>
                        )}
                        {isLatest && (
                          <span style={{ ...badgeBase, background: "#fff", color: meta.color, border: `1px solid ${meta.color}` }}>Latest</span>
                        )}
                        <span
                          title="Updated By"
                          style={{ marginLeft: "auto", fontSize: "11.5px", fontWeight: 700, color: meta.color }}
                        >
                          {showValue(event.changedByName)}
                        </span>
                        <span title={eventTimeTitle} style={{ fontSize: "10.5px", color: "#5b6b7a" }}>
                          {formatDateTime(event.eventTime)}
                        </span>
                      </div>

                      <div style={{ padding: "10px 12px" }}>
                        {assigned && assigned.length > 0 && (
                          <SectionCard icon={ClipboardList} title="Tasks Assigned" palette={sectionPalette.assigned}>
                            {typeof assigned[0] === "object" ? (
                              <AssignedTaskTable rows={assigned} />
                            ) : (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                {assigned.map((name, i) => (
                                  <span key={i} style={{ ...badgeBase, background: "#f1e9fd", color: "#7c3aed", border: "1px solid #ddd0f5" }}>
                                    {name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </SectionCard>
                        )}

                        {claimed && claimed.length > 0 && (
                          <SectionCard icon={LoaderIcon} title="Tasks In Progress" palette={sectionPalette.claimed}>
                            <TaskDetailTable rows={claimed} onPreview={setPreviewFile} onPreviewGallery={openGallery} />
                          </SectionCard>
                        )}

                        {completed && completed.length > 0 && (
                          <SectionCard icon={CheckCircle2} title="Tasks Completed" palette={sectionPalette.completed}>
                            <TaskDetailTable rows={completed} onPreview={setPreviewFile} onPreviewGallery={openGallery} />
                          </SectionCard>
                        )}

                        {!hasDetails && <div style={{ fontSize: "11px", color: "#8a97a3" }}>No additional details.</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eef1f4", display: "flex", justifyContent: "flex-end", background: "#f8fafc" }}>
          <button
            type="button"
            onClick={onClose}
            style={{ height: "32px", fontSize: "12px", fontWeight: 600, padding: "0 18px", borderRadius: "6px", cursor: "pointer", border: "1px solid #2E6DA4", color: "#fff", background: "#2E6DA4" }}
          >
            Close
          </button>
        </div>
      </div>

      {previewFile && (
        <div onClick={(e) => e.stopPropagation()}>
          <PreviewFile
            show={!!previewFile}
            file={previewFile}
            title={previewFile?.name}
            previewHandler={() => setPreviewFile(null)}
          />
        </div>
      )}

      {showDescription && (
        <div
          onClick={(e) => { e.stopPropagation(); setShowDescription(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(17,28,38,0.55)", zIndex: 3600,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
            fontFamily: "'Poppins', 'Segoe UI', sans-serif",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "10px", width: "min(420px, 92vw)", boxShadow: "0 12px 40px rgba(0,0,0,0.28)", overflow: "hidden" }}
          >
            <div style={{ display: "flex", alignItems: "center", padding: "10px 14px", background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)" }}>
              <span style={{ flex: 1, fontSize: "13px", fontWeight: 800, color: "#fff" }}>Incident Details</span>
              <span onClick={() => setShowDescription(false)} title="Close" style={{ cursor: "pointer", display: "flex" }}>
                <X size={16} color="#fff" />
              </span>
            </div>
            <div style={{ padding: "14px", fontSize: "12px", color: "#1D2939", lineHeight: 1.6, maxHeight: "55vh", overflowY: "auto" }}>
              {[
                ["Incident ID", historyData?.incident?.incidentSeqId],
                ["Incident Name", historyData?.incident?.incidentName],
                ["Hazard Type", historyData?.incident?.hazardType],
                ["Severity", historyData?.incident?.severity],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={{ display: "flex", gap: "8px", marginBottom: "4px" }}>
                  <strong style={{ width: "96px", flexShrink: 0, color: "#33414d", fontWeight: 600 }}>{label}</strong>
                  <span style={{ wordBreak: "break-word" }}>{value}</span>
                </div>
              ))}
              <div style={{ marginTop: "8px", fontWeight: 600, color: "#33414d" }}>Description</div>
              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{historyData?.incident?.description}</div>
            </div>
          </div>
        </div>
      )}

      {galleryImages && (
        <div onClick={(e) => e.stopPropagation()}>
          <AttachmentGalleryModal
            images={galleryImages}
            index={galleryIndex}
            onIndexChange={setGalleryIndex}
            onClose={() => setGalleryImages(null)}
          />
        </div>
      )}
    </div>
  );
};

export default SopInstanceAuditHistoryModal;
